import { Icon } from '@components/icon/Icon';
import { useDraggable } from '@dnd-kit/core';
import cx from 'classnames';
import { deriveTableDisplayName } from '@shared/utils/deriveTableDisplayName';
import { type MouseEventHandler } from 'react';
import { useRestaurant } from '../../../context/useRestaurant';
import type { ServiceReservation } from '../apiHelpers';
import { ServiceStatusSelect } from '../serviceStatus/ServiceStatusSelect';
import styles from './ActiveReservationCard.scss';
import { OccupantCardContent } from './OccupantCardContent';
import { getReservationCardDisplayIsoTime } from './getReservationCardDisplayIsoTime';
import { getFullName } from '@shared/utils/formatName';

interface ReservationCardProps {
  isSelected?: boolean;
  onClick: MouseEventHandler<HTMLButtonElement>;
  reservation: ServiceReservation;
  floorPlanName: string;
}

export const ActiveReservationCard = ({
  isSelected = false,
  onClick,
  reservation,
  floorPlanName,
}: ReservationCardProps) => {
  const { timezone } = useRestaurant();

  const { attributes, listeners, setNodeRef } = useDraggable({
    data: {
      reservation,
      type: 'reservation',
    },
    id: reservation.id,
    attributes: {
      role: 'article',
    },
  });

  const {
    guest,
    guestCount,
    hasDietaryRestrictionOrAllergy,
    hasGuestNote,
    isMultiTable,
    isVIP,
    listingName,
    note,
    seatedTableName,
    serviceStatus,
    server,
    transactionDetails,
    eventName,
    wasTableChosenAtTimeOfBooking,
  } = reservation;

  const isHostBooked = !!reservation.transactionDetails.createdBy.fullName;
  const isPaid = transactionDetails.price > 0;
  const hasEvent = !!eventName;
  const showPaidCorner = isPaid && !hasEvent;
  const showChosenTableCorner =
    !isPaid && wasTableChosenAtTimeOfBooking && !hasEvent;

  const guestName = getFullName(
    reservation.guest.firstName,
    reservation.guest.lastName,
  );

  const tableName = deriveTableDisplayName({
    seatedTableName,
    isMultiTable,
    listingName,
  });

  const cardTime = getReservationCardDisplayIsoTime(reservation, timezone);

  return (
    <article
      aria-current={isSelected}
      aria-label={`reservation card for ${guest.firstName} ${guest.lastName}`}
      className={styles.card}
      ref={setNodeRef}
      {...listeners}
      {...attributes}
    >
      {showPaidCorner && (
        <div
          className={cx(
            styles.cornerIcon,
            wasTableChosenAtTimeOfBooking
              ? styles.paid
              : styles.paidNoTableChosen,
          )}
          aria-hidden="true"
          data-testid="paid-reservation-icon"
        >
          <Icon name="dollar" role="img" ariaLabel="Paid reservation" />
        </div>
      )}
      {showChosenTableCorner && (
        <div
          className={cx(styles.cornerIcon, styles.chosenTable)}
          aria-hidden="true"
          data-testid="chosen-table-icon"
        >
          <Icon name="chair" role="img" ariaLabel="Table selected" />
        </div>
      )}
      {hasEvent && (
        <div className={styles.eventIndicatorSection}>
          <Icon name={'ticket'}></Icon>
        </div>
      )}
      <div className={styles.serviceStatusSelect}>
        <ServiceStatusSelect occupant={reservation} value={serviceStatus} />
      </div>
      <button
        aria-label="open details"
        className={styles.button}
        onClick={onClick}
        type="button"
      >
        <OccupantCardContent
          guestCount={guestCount}
          guestName={guestName}
          tableName={tableName}
          floorPlanName={floorPlanName}
          server={server}
          time={cardTime}
          hasDietaryRestrictionOrAllergy={hasDietaryRestrictionOrAllergy}
          hasGuestNote={hasGuestNote}
          hasNoteToRestaurant={!!note}
          hotel={reservation.hotel}
          isHostBooked={isHostBooked}
          isVIP={isVIP}
          lightspeedOrder={reservation.lightspeedOrder ?? null}
        />
      </button>
    </article>
  );
};
